import axios from "../../../api/axios";
import { pdfjs } from "react-pdf";
import { PDFDocument } from "pdf-lib";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const LOCAL_FILE_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

/**
 * Dev API builds file links as http://localhost:4000/uploads/...
 * On Spark that host is not the browser, so the drawing PDF fails to load.
 * Same-origin /uploads is proxied to the API that actually stored the file.
 */
export const proxyLocalUploadUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const trimmed = url.trim();
  if (
    trimmed.startsWith("/uploads") ||
    trimmed.startsWith("/pdfs") ||
    trimmed.startsWith("/rails/")
  ) {
    return trimmed;
  }
  try {
    const parsed = new URL(trimmed);
    const proxied =
      parsed.pathname.startsWith("/uploads") ||
      parsed.pathname.startsWith("/pdfs") ||
      parsed.pathname.startsWith("/rails/");
    if (proxied && LOCAL_FILE_HOSTS.has(parsed.hostname)) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    return trimmed;
  }
  return trimmed;
};

export const resolveDrawingPdfUrl = async (drawingData) => {
  if (drawingData.document?.file?.url) {
    return proxyLocalUploadUrl(drawingData.document.file.url);
  }

  if (drawingData.document?.id) {
    const docResponse = await axios({
      method: "get",
      url: `/documents/${drawingData.document.id}`,
      params: { fields: "id,title,file" },
    });

    if (docResponse.data?.file?.url) {
      return proxyLocalUploadUrl(docResponse.data.file.url);
    }

    if (docResponse.data?.file) {
      return proxyLocalUploadUrl(
        docResponse.data.file.url || docResponse.data.file.full_url
      );
    }
  }

  return null;
};

export const loadPdfBlobUrl = async (pdfFileUrl) => {
  const url = proxyLocalUploadUrl(pdfFileUrl);
  const isAbsoluteUrl =
    url.startsWith("http://") || url.startsWith("https://");
  const isProxiedPath =
    url.startsWith("/uploads") ||
    url.startsWith("/pdfs") ||
    url.startsWith("/rails/");

  if (isAbsoluteUrl || isProxiedPath) {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch PDF: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    await requirePdfBlob(blob);
    return URL.createObjectURL(blob);
  }

  const res = await axios({
    method: "get",
    url,
    responseType: "blob",
  });

  await requirePdfBlob(res.data);
  return URL.createObjectURL(res.data);
};

function readBlobAsText(blob) {
  if (typeof blob?.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function requirePdfBlob(blob) {
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid pdf");
  }
  const header = await readBlobAsText(blob.slice(0, 4));
  if (header !== "%PDF") {
    throw new Error("Invalid pdf");
  }
  return blob;
}

export const normalizeModules = (modules = []) =>
  modules.map((module) => ({
    ...module,
    x_position: parseFloat(module.x_position) || 0,
    y_position: parseFloat(module.y_position) || 0,
    x2_position:
      module.x2_position === null || module.x2_position === undefined
        ? null
        : parseFloat(module.x2_position),
    y2_position:
      module.y2_position === null || module.y2_position === undefined
        ? null
        : parseFloat(module.y2_position),
    width: parseFloat(module.width) || 50,
    height: parseFloat(module.height) || 50,
    path_data: module.path_data || null,
  }));

/**
 * Drawings only support a single PDF page (one page per drawing).
 * Read the page count before upload so the editor can offer to extract one page.
 */
export const getPdfPageCount = async (file) => {
  const data = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data });
  const pdf = await loadingTask.promise;
  const numPages = pdf.numPages;
  await pdf.destroy();
  return numPages;
};

/**
 * Extract a single page (0-based `pageIndex`) into a new single-page PDF File.
 * Client-side MVP alternative to a server-side PDF split.
 */
export const extractPdfPageToFile = async (file, pageIndex) => {
  const bytes = await file.arrayBuffer();
  const sourceDoc = await PDFDocument.load(bytes);
  const targetDoc = await PDFDocument.create();
  const [copiedPage] = await targetDoc.copyPages(sourceDoc, [pageIndex]);
  targetDoc.addPage(copiedPage);
  const newBytes = await targetDoc.save();

  const baseName = file.name.replace(/\.pdf$/i, "");
  const fileName = `${baseName}-side-${pageIndex + 1}.pdf`;
  return new File([newBytes], fileName, { type: "application/pdf" });
};

export const clampModulePosition = (xPos, yPos, moduleWidth, moduleHeight, pdfSize) => {
  if (!pdfSize?.width || !pdfSize?.height) {
    return { x: xPos, y: yPos };
  }

  return {
    x: Math.max(0, Math.min(pdfSize.width - moduleWidth, xPos)),
    y: Math.max(0, Math.min(pdfSize.height - moduleHeight, yPos)),
  };
};
